/**
 * Shared helpers for the "missing data points" breakdown feature (the red "!" markers
 * on the consolidated-performance table in ESGAnalyticsCards and on the unit-wise
 * comparison chart in ESGAnalyticsStackedBarChart). Both drive the same
 * ESGAnalyticsDPBreakdownModal, so the request-building logic lives here once.
 */

export function getBranchScopedFilter(baseFilter, branchId) {
  if (!branchId) return baseFilter;
  return {
    ...baseFilter,
    businessUnitIds: [branchId],
    parentBranchIds: [branchId],
    childBranchIds: [branchId],
    parentEntities: [branchId],
    childEntities: [branchId]
  };
}

/**
 * Column period key (e.g. "2024", or "2024-JAN") → narrowed `filter.period` for that column.
 * `selectedRepPeriod` is unchanged and comes from `...filter` when building the request.
 */
export function cellPeriodKeyToNarrowedPeriod(cellPeriodKey, filterSnapshot) {
  const fullPeriod = filterSnapshot?.period;
  const isYearly = (filterSnapshot?.periodType || 'YEARLY') === 'YEARLY';

  if (cellPeriodKey == null || !fullPeriod || typeof fullPeriod !== 'object') {
    return fullPeriod;
  }

  if (isYearly) {
    const y = String(cellPeriodKey);
    const key = Object.keys(fullPeriod).find(
      (k) => String(k) === y || String(k) === String(parseInt(cellPeriodKey, 10))
    );
    if (key == null) {
      return fullPeriod;
    }
    return { [key]: fullPeriod[key] };
  }

  const dash = String(cellPeriodKey).indexOf('-');
  if (dash <= 0) {
    return fullPeriod;
  }
  const year = String(cellPeriodKey).slice(0, dash);
  const sub = String(cellPeriodKey).slice(dash + 1);
  const yearVals = fullPeriod[year];
  if (!Array.isArray(yearVals) || !yearVals.includes(sub)) {
    return fullPeriod;
  }
  return { [year]: [sub] };
}

export function findConfigRowForLevel1Item(configRows, level1Item) {
  if (!configRows || !level1Item) return null;
  if (level1Item.isEquation && level1Item.equationId) {
    return (
      configRows.find(
        (row) =>
          row.level1?.itemType === 'equation' &&
          row.level1?.eqExpressionWithId === level1Item.equationId
      ) || null
    );
  }
  return configRows.find((row) => row.level1 && row.level1.id === level1Item.id) || null;
}

export function buildNullDatapointBreakdownPayload(configRows, level1Item, branchId, cellPeriodKey, filter) {
  const configRow = findConfigRowForLevel1Item(configRows, level1Item);
  const period = cellPeriodKeyToNarrowedPeriod(cellPeriodKey, filter);
  const base = {
    ...getBranchScopedFilter(
      {
        ...filter,
        period,
        metricIds: [],
        datapointIds: [],
        equations: [],
        equationConfig: [],
        breakByDp: true
      },
      branchId
    )
  };
  if (configRow?.level1?.itemType === 'metric' || configRow?.level1?.itemType === 'equation') {
    return {
      ...base,
      metricIds: [],
      datapointIds: [],
      equations: [configRow.level1.eqExpressionWithId],
    };
  }
  return {
    ...base,
  };
}
